import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQuotations } from "@/api/queries";
import type { QuotationSummary } from "@/api/types";
import { KpiStrip } from "@/components/KpiStrip";
import type { Column } from "@/components/list/DataTable";
import type { ListSearch } from "@/components/list/listQuery";
import { ListScreen, listQueryFor } from "@/components/list/ListScreen";
import { PermittedAction } from "@/components/PermittedAction";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { QuotationStatusBadge } from "./QuotationStatusBadge";

const STATUSES = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "accepted", label: "Accepted" },
  { value: "declined", label: "Declined" },
  { value: "expired", label: "Expired" },
  { value: "converted", label: "Converted" },
];

const countOf = (n: number, what: string) => `${n} ${what}`;

export function QuotationsListPage() {
  const search = useSearch({ from: "/sales/quotations" }) as ListSearch;
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useQuotations(listQueryFor(search));
  const overview = data?.overview;

  const columns: Array<Column<QuotationSummary>> = [
    {
      key: "number",
      header: "Number",
      width: "12ch",
      numeric: true,
      render: (row) => row.number ?? <span className="text-fg-hint">Not yet sent</span>,
    },
    { key: "customer", header: "Customer", render: (row) => row.customerName },
    { key: "date", header: "Date", width: "12ch", render: (row) => formatDate(row.date) },
    {
      key: "validUntil",
      header: "Valid until",
      width: "12ch",
      render: (row) => (
        <span className={row.status === "expired" ? "text-bad-text" : undefined}>
          {formatDate(row.validUntil)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "18ch",
      render: (row) => (
        <span className="flex items-center gap-1.5">
          <QuotationStatusBadge status={row.status} />
          {row.invoiceNumber ? (
            <span className="num text-fg-hint text-xs">{row.invoiceNumber}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: "total",
      header: "Amount",
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.total),
    },
  ];

  return (
    <ListScreen
      route="/sales/quotations"
      title="Quotations"
      subtitle={
        overview
          ? `${countOf(overview.awaitingCount, "awaiting a response")} · ${countOf(overview.expiredCount, "expired")}`
          : "Loading quotations"
      }
      actions={
        <PermittedAction
          permission="sales.quotations.create"
          leftIcon="Plus"
          onClick={() => void navigate({ to: "/sales/quotations/new" })}
        >
          New quotation
        </PermittedAction>
      }
      summary={
        overview ? (
          <KpiStrip
            items={[
              {
                label: "Awaiting a response",
                value: formatAmount(overview.awaitingAmount),
                detail: countOf(overview.awaitingCount, "sent"),
                to: "/sales/quotations",
                search: { status: "sent" },
              },
              {
                label: "Accepted, not yet invoiced",
                value: formatAmount(overview.acceptedAmount),
                detail: countOf(overview.acceptedCount, "ready to convert"),
                to: "/sales/quotations",
                search: { status: "accepted" },
              },
              {
                label: "Expired",
                value: String(overview.expiredCount),
                detail: "validity date passed",
                tone: overview.expiredCount > 0 ? "warn" : "default",
                to: "/sales/quotations",
                search: { status: "expired" },
              },
            ]}
          />
        ) : null
      }
      statuses={STATUSES}
      columns={columns}
      rows={data?.items ?? []}
      total={data?.total ?? 0}
      hasNext={data?.next != null}
      loading={isPending}
      error={error}
      onRetry={() => void refetch()}
      rowKey={(row) => row.id}
      rowLabel={(row) =>
        `Quotation ${row.number ?? "not yet sent"}, ${row.customerName}, ${formatAmount(row.total)}`
      }
      onRowClick={(row) => void navigate({ to: "/sales/quotations/$id", params: { id: row.id } })}
      searchPlaceholder="Search number, customer or reference"
      empty={{
        icon: "FilePenLine",
        title: "No quotations yet",
        description: "A quotation is an offer to a customer. It posts nothing until it's invoiced.",
      }}
      filteredEmpty={{
        icon: "Search",
        title: "No quotations match these filters",
        description: "Try a wider date range, or clear the filters to see everything again.",
      }}
    />
  );
}
